import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import {useTable, useGlobalFilter, useSortBy, useResizeColumns, usePagination} from "react-table";
import { FiEdit, FiTrash, FiArrowLeft } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import "./SubTopics.css";
import "./employerPortal.css";

export default function SubTopics() {
  const navigate = useNavigate();
  const { topic_id } = useParams();

  const [subTopics, setSubTopics] = useState([]);
  const [subTopicFilter, setSubTopicFilter] = useState("");
  const [editingSubTopic, setEditingSubTopic] = useState(null);
  const [editedSubTopic, setEditedSubTopic] = useState({});
  const [subTopicGlobalFilter, setSubTopicGlobalFilter] = useState("");

  useEffect(() => {
    // Fetch topics
    AxiosInstance
      .get(`companies/sub-topics/${topic_id}/`)
      .then((response) => {
        setSubTopics(response.data);
      })
      .catch((error) => {
        console.error("Error fetching topics:", error);
      });
  }, [topic_id]);

  function formatContent(text) {
    if (!text) return "";

    // Convert lines starting with "-" or "*" into <li> items
    const lines = text.split("\n");

    const listItems = lines.map((line) => {
      const trimmed = line.trim();
      if (trimmed.startsWith("-") || trimmed.startsWith("*")) {
        return `<li>${trimmed.substring(1).trim()}</li>`;
      } else {
        return `<p>${trimmed}</p>`;
      }
    });

    // Wrap <li> items inside <ul> if any exist
    const ulItems = listItems.filter((item) => item.startsWith("<li>"));
    const otherItems = listItems.filter((item) => !item.startsWith("<li>"));

    const listHtml = ulItems.length > 0 ? `<ul>${ulItems.join("")}</ul>` : "";
    return `${listHtml}${otherItems.join("")}`;
  }

  const subTopicColumns = useMemo(
    () => [
      { Header: "Topic Name", accessor: "topic.topic_name", resizable: true },
      { Header: "Sub Topic Name", accessor: "sub_topic_name", resizable: true },
      {
        Header: "Sub Topic Content",
        accessor: "sub_topic_content",
        resizable: true,
        Cell: ({ value }) => (
          <div
            dangerouslySetInnerHTML={{ __html: formatContent(value) }}
            style={{ whiteSpace: "normal", lineHeight: "1.5" }}
          />
        ),
      },
      { Header: "Video Link", accessor: "video_link", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const original = row.original;
                setEditingSubTopic(original);
                setEditedSubTopic(original);
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteSubTopic(row.original.sub_topic_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps: getSubTopicTableProps,
    getTableBodyProps: getSubTopicTableBodyProps,
    headerGroups: subTopicHeaderGroups,
    prepareRow: prepareSubTopicRow,
    page: subTopicPage,
    canPreviousPage: canPreviousSubTopicPage,
    canNextPage: canNextSubTopicPage,
    pageOptions: subTopicPageOptions,
    nextPage: nextSubTopicPage,
    previousPage: previousSubTopicPage,
    state: { pageIndex: subTopicPageIndex },
    setGlobalFilter: setSubTopicGlobalFilterFunc,
  } = useTable(
    {
      columns: subTopicColumns,
      data: subTopics,
      initialState: { pageSize: 8 },
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setSubTopicGlobalFilterFunc(subTopicGlobalFilter);
  }, [subTopicGlobalFilter, setSubTopicGlobalFilterFunc]);

  const handleSaveSubTopic = async () => {
    const formData = new FormData();
    formData.append("sub_topic_name", editedSubTopic.sub_topic_name);
    formData.append("topic", editedSubTopic.topic.topic_id);
    formData.append("sub_topic_content", editedSubTopic.sub_topic_content);

    if (editedSubTopic.video_file) {
      formData.append("video_link", editedSubTopic.video_file);
    }

    try {
      const response = await AxiosInstance.put(
        `companies/sub-topic/${editedSubTopic.sub_topic_id}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Sub topic updated", response.data);
      setEditingSubTopic(null);
      const refreshed = await AxiosInstance.get(
        `companies/sub-topics/${topic_id}/`
      );
      setSubTopics(refreshed.data);
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  const handleDeleteSubTopic = (subTopicId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this sub topic?"
    );
    if (!confirmDelete) return;

    AxiosInstance
      .delete(`companies/topic/sub-topics/${subTopicId}/`)
      .then(() => AxiosInstance.get(`companies/sub-topics/${topic_id}/`))
      .then((response) => setSubTopics(response.data))
      .catch((error) => console.error("Error deleting sub topic:", error));
  };

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={() => navigate(-1)}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        <h2 className="section-heading">Sub Topics:</h2>
        <div className="card-custom">
          <div className="d-flex justify-content-between mb-3">
            <input
              type="text"
              className="form-control w-50"
              value={subTopicFilter}
              onChange={(e) => {
                setSubTopicFilter(e.target.value);
                setSubTopicGlobalFilter(e.target.value);
              }}
              placeholder="Search sub topic..."
            />
            <button
              className="add-button"
              onClick={() => navigate(`/sub-topics`)}
            >
              Add
            </button>
          </div>

          <div className="table-responsive">
            <table
              {...getSubTopicTableProps()}
              className="table table-bordered table-striped"
            >
              <thead className="table-light">
                {subTopicHeaderGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    key={headerGroup.id}
                  >
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        key={column.id}
                      >
                        <div {...column.getHeaderProps()}>
                          {column.render("Header")}
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " ▼"
                              : " ▲"
                            : ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getSubTopicTableBodyProps()}>
                {subTopicPage.length === 0 ? (
                  <tr>
                    <td colSpan={subTopicColumns.length}>
                      No sub topics found.
                    </td>
                  </tr>
                ) : (
                  subTopicPage.map((row) => {
                    prepareSubTopicRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

            <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-3">
              <button
                onClick={() => previousSubTopicPage()}
                disabled={!canPreviousSubTopicPage}
                className="btn btn-outline-primary"
              >
                Previous
              </button>
              <span>
                Page{" "}
                <strong>
                  {subTopicPageIndex + 1} of {subTopicPageOptions.length}
                </strong>
              </span>
              <button
                onClick={() => nextSubTopicPage()}
                disabled={!canNextSubTopicPage}
                className="btn btn-outline-primary"
              >
                Next
              </button>
            </div>
          {editingSubTopic && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Sub Topic</h5>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <label className="form-label">Topic Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="topic"
                          value={editedSubTopic.topic?.topic_name || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              topic: {
                                ...editedSubTopic.topic,
                                topic_name: e.target.value,
                              },
                            })
                          }
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Sub Topic Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="sub_topic_name"
                          value={editedSubTopic.sub_topic_name || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              sub_topic_name: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Sub Topic Content</label>
                        <textarea
                          className="form-control"
                          name="sub_topic_content"
                          rows="6"
                          placeholder="Write sub-topic content here. Use - or * for bullets, or separate paragraphs with new lines."
                          value={editedSubTopic.sub_topic_content || ""}
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              sub_topic_content: e.target.value,
                            })
                          }
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Video File</label>
                        <input
                          type="file"
                          className="form-control"
                          name="video_link"
                          onChange={(e) =>
                            setEditedSubTopic({
                              ...editedSubTopic,
                              video_file: e.target.files[0],
                            })
                          }
                        />
                        {editedSubTopic.video_link && (
                          <div className="form-text">
                            Current file:{" "}
                            {editedSubTopic.video_link.split("/").pop()}
                          </div>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setEditingSubTopic(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveSubTopic}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
